import { createContext, MutableRefObject, useContext, useMemo, useRef, useState } from 'react';

import { DEFAULT_VENDOR_SELECTION_LIMIT } from '~/constants';
import { MarketplaceVendor } from '~/types/marketplace';

export const VendorContext = createContext({
  hasVendorsSelected: false,
  isAutoSelecting: false,
  previouslySelectedVendorIds: [] as string[],
  previouslySelectedVendors: [] as MarketplaceVendor[],
  requestQuoteModal: false,
  reviewQuoteRef: null as unknown as MutableRefObject<HTMLButtonElement | null>,
  selectedVendors: [] as MarketplaceVendor[],
  vendorSelectionLimitReached: false,
  addPreviouslySelectedVendor: (_vendor: MarketplaceVendor) => {},
  setIsAutoSelecting: (_value: boolean | ((prevState: boolean) => boolean)) => {},
  setRequestQuoteModal: (_value: boolean | ((prevState: boolean) => boolean)) => {},
  setSelectedVendors: (_vendors: MarketplaceVendor[]) => {},
});

export const VendorProvider = ({
  children,
  previouslySelectedVendorIds = [],
}: {
  children: React.ReactNode;
  previouslySelectedVendorIds?: string[];
}) => {
  const [selectedVendors, setSelectedVendors] = useState<MarketplaceVendor[]>([]);
  const [previouslySelectedVendors, setPreviouslySelectedVendors] = useState<MarketplaceVendor[]>([]);
  const [requestQuoteModal, setRequestQuoteModal] = useState<boolean>(false);
  const [isAutoSelecting, setIsAutoSelecting] = useState<boolean>(false);

  const addPreviouslySelectedVendor = (vendor: MarketplaceVendor) => {
    setPreviouslySelectedVendors((prev) =>
      prev.some((v) => v.publicId === vendor.publicId) ? prev : [...prev, vendor],
    );
  };
  const reviewQuoteRef = useRef<HTMLButtonElement | null>(null);

  const hasVendorsSelected = useMemo(
    () => previouslySelectedVendorIds.length > 0 || selectedVendors.length > 0,
    [previouslySelectedVendorIds, selectedVendors],
  );
  const vendorSelectionLimitReached = useMemo(
    () => previouslySelectedVendorIds.length + selectedVendors.length >= DEFAULT_VENDOR_SELECTION_LIMIT,
    [previouslySelectedVendorIds, selectedVendors],
  );

  const contextValue = useMemo(
    () => ({
      hasVendorsSelected,
      previouslySelectedVendorIds,
      previouslySelectedVendors,
      isAutoSelecting,
      requestQuoteModal,
      reviewQuoteRef,
      selectedVendors,
      vendorSelectionLimitReached,
      addPreviouslySelectedVendor,
      setIsAutoSelecting,
      setRequestQuoteModal,
      setSelectedVendors,
    }),
    [
      hasVendorsSelected,
      isAutoSelecting,
      previouslySelectedVendorIds,
      previouslySelectedVendors,
      requestQuoteModal,
      selectedVendors,
      vendorSelectionLimitReached,
    ],
  );

  return <VendorContext.Provider value={contextValue}>{children}</VendorContext.Provider>;
};

export const useVendorContext = () => {
  const context = useContext(VendorContext);
  if (context === undefined) {
    throw new Error('useVendorContext must be used within a VendorProvider');
  }
  return context;
};
